import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const ThemeContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'theme';
const MEDIA = '(prefers-color-scheme: light)';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

/*
 * Theme source of truth.
 *
 *  preference: 'light' | 'dark' | 'system'
 *  theme:      the resolved 'light' | 'dark' actually painted
 *
 * localStorage 'theme' holds 'light' | 'dark', or is absent for 'system'.
 * Precedence: explicit local choice > the signed-in user's server preference
 * (only an explicit 'light' | 'dark') > OS setting. The pre-paint script in
 * public/index.html reads 'theme' before React loads, so there is no flash;
 * data-theme is written synchronously on every change after that.
 *
 * A value copied from the server is also kept in 'theme' (so the pre-paint
 * script avoids a flash on reload) but tagged in 'theme_origin' with the
 * owning user id. It is not treated as a local choice: it is dropped on sign
 * out or when a different account signs in, and refreshed on every sign-in.
 */
const ORIGIN_KEY = 'theme_origin';
const SERVER_ORIGIN = 'server:';

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
};

const readOrigin = () => {
  try {
    return localStorage.getItem(ORIGIN_KEY) || '';
  } catch {
    return '';
  }
};

// An explicit choice made on this device (not copied from an account).
const readExplicit = () => (readOrigin().startsWith(SERVER_ORIGIN) ? null : readStored());

const writeStored = (value, origin) => {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
    if (origin) localStorage.setItem(ORIGIN_KEY, origin);
    else localStorage.removeItem(ORIGIN_KEY);
  } catch { /* storage blocked: the choice still applies for this session */ }
};

const systemTheme = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia(MEDIA).matches ? 'light' : 'dark';

const applyTheme = (resolved) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  // index.html ships one theme-color per OS scheme; an explicit choice overrides both.
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute('content', resolved === 'light' ? '#f7f9fc' : '#0a0f1a');
  });
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() => readStored() || 'system');
  const [system, setSystem] = useState(systemTheme);
  const theme = preference === 'system' ? system : preference;
  // Bumped when the user picks a theme in this tab, so a slower server response can't override it.
  const touchRef = useRef(0);
  const userId = useAuth().user?.id || null;
  const preferenceRef = useRef(preference);
  preferenceRef.current = preference;

  // Paint before the browser draws the new frame (no flash, and charts that read
  // tokens during render see the right values).
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // 'system' follows the OS live.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(MEDIA);
    const onChange = () => {
      const next = mq.matches ? 'light' : 'dark';
      if (preferenceRef.current === 'system') applyTheme(next);
      setSystem(next);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Another tab changed the theme.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      const pref = readStored() || 'system';
      applyTheme(pref === 'system' ? systemTheme() : pref);
      setPreference(pref);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Server preference: re-read whenever the signed-in account changes, and
  // applied only when there is no explicit local choice.
  useEffect(() => {
    const fallBackToLocal = () => {
      if (!readOrigin().startsWith(SERVER_ORIGIN)) return;
      writeStored(null, null);
      applyTheme(systemTheme());
      setPreference('system');
    };
    const token = localStorage.getItem('token');
    if (!userId || !token) {
      // Signed out: forget the previous account's theme.
      fallBackToLocal();
      return undefined;
    }
    if (readExplicit()) return undefined;
    if (readOrigin() !== SERVER_ORIGIN + userId) fallBackToLocal();

    let cancelled = false;
    const touchedAt = touchRef.current;
    axios
      .get(`${API}/user/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        // A choice made while the request was in flight wins.
        if (cancelled || touchRef.current !== touchedAt || readExplicit()) return;
        const server = res.data?.theme;
        if (server === 'light' || server === 'dark') {
          writeStored(server, SERVER_ORIGIN + userId);
          applyTheme(server);
          setPreference(server);
        } else {
          fallBackToLocal();
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const setTheme = useCallback((next) => {
    const pref = next === 'light' || next === 'dark' ? next : 'system';
    touchRef.current += 1;
    writeStored(pref === 'system' ? null : pref, null);
    // Write the attribute now so anything rendering in this update reads the new tokens.
    applyTheme(pref === 'system' ? systemTheme() : pref);
    setPreference(pref);

    const token = localStorage.getItem('token');
    if (token) {
      axios
        .put(`${API}/user/preferences`, { theme: pref }, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, preference, setTheme, toggleTheme }),
    [theme, preference, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
